import { Mic2, Square, Disc3, Cable, Zap, Plus, X, Share2 } from "lucide-react";
import InputDevicePicker from "./InputDevicePicker";
import QuickArmCommunitySection from "./QuickArmCommunitySection";

/**
 * Tracking controls — input device picker + Arm All / Disarm All / Record All.
 * Renders below the mixer so users can configure multi-track recording at a glance.
 *
 * iter-206 — Quick Arm preset row: one-tap buttons that arm the first N
 * channels AND round-robin them across the available inputs, then pop
 * the Routing Matrix open so the user can confirm before pressing Record.
 *
 * iter-207 — User-saved presets join the row alongside the built-in
 * Stereo/Drums/Band trio. "+ Save current" pill captures the current
 * armed-channels + sourceMap to localStorage; saved presets render as
 * cyan-tinted pills with a delete-on-hover X.
 *
 * iter-208 — Community marketplace: a "Community" expand-toggle pill
 * reveals public arm-routing templates shared by other users, with
 * sort + category filters, like-hearts, and one-tap apply. Each user-
 * saved preset gets a small Share icon to publish it to the marketplace.
 */
export default function TrackingControlsBar({
  armedCount,
  tracking,
  inputDeviceId,
  onInputDeviceChange,
  onArmAll,
  onDisarmAll,
  onRecord,
  streamToDisk = false,
  onToggleStreamToDisk,
  sessionId = null,
  bytesStreamed = 0,
  onOpenRoutingMatrix,
  onQuickArmTemplate,
  customArmPresets = [],
  onSaveCustomArmPreset,
  onApplyCustomArmPreset,
  onRemoveCustomArmPreset,
  // iter-208 — community marketplace props
  onSharePreset,             // (preset) => void  (opens publish dialog)
  communityTemplates = [],
  communityLoading = false,
  communityError = null,
  communitySort = "popular",
  setCommunitySort,
  communityCategory = null,
  setCommunityCategory,
  myUserId = null,
  onApplyCommunityPreset,
  onToggleCommunityLike,
  onDeleteCommunityPreset,
}) {
  const canRecord = armedCount > 0;
  const streamed = bytesStreamed < 1024 * 1024
    ? `${Math.round(bytesStreamed / 1024)} KB`
    : `${(bytesStreamed / (1024 * 1024)).toFixed(1)} MB`;
  // iter-206 — Quick Arm template presets. Order matches typical workflow
  // size (stereo pair → drum kit → full band). Each template arms the
  // first N channels, distributes them round-robin across inputs, and
  // opens the Routing Matrix modal so the user can confirm.
  const QUICK_ARM_TEMPLATES = [
    { id: "stereo", label: "Stereo", count: 2, hint: "Stereo pair · 2 channels" },
    { id: "drums",  label: "Drums",  count: 4, hint: "Drum kit · 4 channels (KK/SD/OH-L/OH-R)" },
    { id: "band",   label: "Band",   count: 8, hint: "Full band · 8 channels (drums + bass + 2 gtr + 2 vox)" },
  ];
  return (
    <div
      data-testid="tracking-controls"
      className={`flex flex-wrap items-center gap-2 rounded-2xl border px-3 py-2 ${
        tracking ? "border-rose-400/50 bg-rose-500/[0.06] shadow-[0_0_24px_rgba(244,63,94,0.18)]" : "border-white/10 bg-white/[0.04]"
      }`}
    >
      <div className="flex items-center gap-2">
        <Disc3 className={`h-4 w-4 ${tracking ? "animate-spin text-rose-300" : "text-fuchsia-300"}`} />
        <span className="font-display text-sm font-semibold tracking-tight">
          {tracking ? "Tracking…" : "Multi-track Tracking"}
        </span>
        <span
          data-testid="tracking-armed-count"
          className={`rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider ${
            armedCount > 0
              ? "bg-rose-500/20 text-rose-200 ring-1 ring-rose-400/30"
              : "bg-white/[0.06] text-white/45"
          }`}
        >
          {armedCount} armed
        </span>
        {tracking && streamToDisk && (
          <span
            data-testid="tracking-bytes-streamed"
            className="rounded-full bg-cyan-500/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-cyan-100 ring-1 ring-cyan-400/30"
            title="Streamed to backend disk"
          >
            {streamed} · {sessionId?.slice(0, 8) || "—"}
          </span>
        )}
      </div>

      <InputDevicePicker
        selectedDeviceId={inputDeviceId}
        onChange={onInputDeviceChange}
      />

      <label
        data-testid="tracking-stream-toggle"
        title="Stream every chunk straight to backend disk so hour-long sessions don't fill browser memory."
        className={`flex cursor-pointer items-center gap-1.5 rounded-xl border px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-wider transition ${
          streamToDisk
            ? "border-cyan-400/40 bg-cyan-500/15 text-cyan-100"
            : "border-white/10 bg-white/[0.04] text-white/65 hover:bg-white/[0.08]"
        } ${tracking ? "pointer-events-none opacity-60" : ""}`}
      >
        <input
          type="checkbox"
          data-testid="tracking-stream-toggle-input"
          checked={streamToDisk}
          onChange={(e) => onToggleStreamToDisk?.(e.target.checked)}
          className="h-3 w-3 accent-cyan-400"
          disabled={tracking}
        />
        Stream to disk
      </label>

      <div className="ml-auto flex items-center gap-2">
        <button
          type="button"
          data-testid="tracking-routing-matrix-btn"
          onClick={onOpenRoutingMatrix}
          disabled={armedCount === 0 || tracking}
          title={armedCount === 0
            ? "Arm one or more channels first"
            : "Open the input-routing matrix — map each mic input to a DAW channel"}
          className="flex items-center gap-1.5 rounded-xl bg-white/[0.06] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white/75 transition hover:bg-white/[0.12] disabled:opacity-40"
        >
          <Cable className="h-3 w-3" />
          Routing
        </button>
        <button
          type="button"
          data-testid="tracking-arm-all-btn"
          onClick={onArmAll}
          disabled={tracking}
          className="rounded-xl bg-white/[0.06] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white/75 hover:bg-white/[0.12] disabled:opacity-40"
        >
          Arm Visible
        </button>
        <button
          type="button"
          data-testid="tracking-disarm-all-btn"
          onClick={onDisarmAll}
          disabled={armedCount === 0 || tracking}
          className="rounded-xl bg-white/[0.06] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white/75 hover:bg-white/[0.12] disabled:opacity-40"
        >
          Disarm All
        </button>
        <button
          type="button"
          data-testid="tracking-record-btn"
          onClick={onRecord}
          disabled={!canRecord && !tracking}
          className={`flex items-center gap-1.5 rounded-xl px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider ring-1 transition ${
            tracking
              ? "bg-rose-500 text-white ring-rose-300/40 hover:bg-rose-400"
              : canRecord
                ? "bg-rose-500/30 text-rose-100 ring-rose-400/40 hover:bg-rose-500/50"
                : "bg-white/[0.04] text-white/40 ring-white/10"
          }`}
        >
          {tracking ? <Square className="h-3 w-3" /> : <Mic2 className="h-3 w-3" />}
          {tracking ? "Stop · Decode all" : streamToDisk ? "Record All to Disk" : "Record All Armed"}
        </button>
      </div>
      {/* iter-206 — Quick Arm preset row · only shown when NOT actively
          tracking. Uses w-full + mt-1 so it wraps onto its own line below
          the main flex-wrap row regardless of viewport width.
          iter-207 — extended to render user-saved custom presets +
          a "+ Save current" pill that captures armed-channels+sourceMap. */}
      {!tracking && (
        <div
          data-testid="tracking-quick-arm-row"
          className="mt-1 flex w-full flex-wrap items-center gap-2 border-t border-white/[0.05] pt-2"
        >
          <span className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-white/45">
            <Zap className="h-3 w-3 text-amber-300" />
            Quick Arm
          </span>
          {QUICK_ARM_TEMPLATES.map((t) => (
            <button
              key={`quick-arm-${t.id}`}
              type="button"
              data-testid={`tracking-quick-arm-${t.id}`}
              onClick={() => onQuickArmTemplate?.(t.count, `${t.label} preset`)}
              title={t.hint}
              className="flex items-center gap-1 rounded-xl border border-white/10 bg-white/[0.04] px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-white/80 transition hover:border-amber-400/40 hover:bg-amber-500/[0.08] hover:text-amber-100"
            >
              <span className="rounded bg-white/10 px-1 text-[9px] font-bold tabular-nums text-white">
                {t.count}
              </span>
              {t.label}
            </button>
          ))}
          {customArmPresets.length > 0 && (
            <span
              className="font-mono text-[9px] uppercase tracking-wider text-white/30"
              aria-hidden="true"
            >
              ·
            </span>
          )}
          {/* iter-207 — Custom user-saved presets (cyan tint, delete-on-hover X).
              iter-208 — also includes a Share button to publish to the community. */}
          {customArmPresets.map((preset) => (
            <div
              key={preset.id}
              data-testid={`tracking-quick-arm-custom-${preset.id}`}
              className="group flex items-center gap-0 rounded-xl border border-cyan-400/30 bg-cyan-500/[0.08] font-mono text-[10px] uppercase tracking-wider text-cyan-100 transition hover:border-cyan-300/60 hover:bg-cyan-500/[0.16]"
            >
              <button
                type="button"
                data-testid={`tracking-quick-arm-custom-apply-${preset.id}`}
                onClick={() => onApplyCustomArmPreset?.(preset)}
                title={`Apply "${preset.name}" — ${preset.channels?.length || 0} channel${preset.channels?.length === 1 ? "" : "s"}`}
                className="flex items-center gap-1 rounded-l-xl py-1 pl-2.5 pr-2"
              >
                <span className="rounded bg-cyan-400/30 px-1 text-[9px] font-bold tabular-nums text-white">
                  {preset.channels?.length || 0}
                </span>
                <span className="max-w-[110px] truncate">{preset.name}</span>
              </button>
              <button
                type="button"
                data-testid={`tracking-quick-arm-custom-share-${preset.id}`}
                onClick={(e) => { e.stopPropagation(); onSharePreset?.(preset); }}
                title={`Share "${preset.name}" to the community marketplace`}
                className="grid h-6 w-6 place-items-center text-cyan-200/60 opacity-0 transition group-hover:opacity-100 hover:bg-fuchsia-500/30 hover:text-fuchsia-100"
                aria-label={`Share preset ${preset.name}`}
              >
                <Share2 className="h-3 w-3" />
              </button>
              <button
                type="button"
                data-testid={`tracking-quick-arm-custom-remove-${preset.id}`}
                onClick={(e) => { e.stopPropagation(); onRemoveCustomArmPreset?.(preset.id); }}
                title={`Delete "${preset.name}"`}
                className="grid h-6 w-6 place-items-center rounded-r-xl text-cyan-200/60 opacity-0 transition group-hover:opacity-100 hover:bg-rose-500/30 hover:text-rose-100"
                aria-label={`Delete preset ${preset.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {/* iter-207 — "+ Save current" pill. Enabled only when the user
              has armed at least one channel; otherwise grey + disabled. */}
          <button
            type="button"
            data-testid="tracking-quick-arm-save"
            onClick={onSaveCustomArmPreset}
            disabled={armedCount === 0}
            title={armedCount === 0
              ? "Arm some channels first, then save them as a personal preset"
              : `Save the current ${armedCount}-channel arm + routing as a personal preset`}
            className="flex items-center gap-1 rounded-xl border border-dashed border-white/20 bg-transparent px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-white/65 transition hover:border-cyan-300/60 hover:bg-cyan-500/[0.06] hover:text-cyan-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/20 disabled:hover:bg-transparent disabled:hover:text-white/65"
          >
            <Plus className="h-3 w-3" />
            Save current
          </button>
          <span className="font-mono text-[9px] uppercase tracking-wider text-white/35">
            · pre-balanced routing
          </span>
          {/* iter-208 — Community marketplace expander. Renders its own
              toggle pill + collapsible panel inline within the Quick Arm row. */}
          <QuickArmCommunitySection
            templates={communityTemplates}
            loading={communityLoading}
            error={communityError}
            sort={communitySort}
            setSort={setCommunitySort}
            category={communityCategory}
            setCategory={setCommunityCategory}
            myUserId={myUserId}
            onApply={onApplyCommunityPreset}
            onToggleLike={onToggleCommunityLike}
            onDelete={onDeleteCommunityPreset}
          />
        </div>
      )}
    </div>
  );
}
